import {
  default as MuiFormLabel,
  type FormLabelProps as MuiFormLabelProps,
} from '@mui/material/FormLabel';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';
import { DistributiveOmit } from '@mui/types';

/**
 * Props that we do not want to include with FormLabel.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type FormLabelBlockedProps = keyof Pick<MuiFormLabelProps, 'filled'>;

export interface FormLabelTypeMap<
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {},
  RootComponent extends React.ElementType = 'label'
> {
  props: AdditionalProps &
    DistributiveOmit<MuiFormLabelProps, FormLabelBlockedProps>;
  defaultComponent: RootComponent;
}

export type FormLabelProps<
  RootComponent extends React.ElementType = FormLabelTypeMap['defaultComponent'],
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {}
> = OverrideProps<
  FormLabelTypeMap<AdditionalProps, RootComponent>,
  RootComponent
> & {
  component?: React.ElementType;
};

/**
 * FormLabel displays a label for form controls.
 *
 * Demos:
 *
 * - [Radio Button](https://unify.workiva.org/components/radio-button?tab=code)
 *
 * API:
 *
 * - [FormLabel API](https://unify.workiva.org/components/radio-button?tab=props#formlabel)
 */
export const FormLabel: OverridableComponent<FormLabelTypeMap> = MuiFormLabel;
